import { useRef, useState } from "react"
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa"
import UploadIcon from "../../../assets/icons/upload.svg"

interface ImageUploadBoxProps {
    productUploadNameRef: React.RefObject<HTMLInputElement>
    categoryToUpload: { label: string; value: string } | null
    productCatError: boolean
    productUploadOtherNames: string[]
    setUploadProductName: (name: string) => void
    setImageUploadData: (data: {
        ProductName: string
        CategoryName: string
        ImageFile: File
        OtherNames: string[]
    }) => void
}


const ImageUploadBox = ({
    productUploadNameRef,
    categoryToUpload,
    productCatError,
    productUploadOtherNames,
    setUploadProductName,
    setImageUploadData,
}: ImageUploadBoxProps) => {
    const inputRef = useRef<HTMLInputElement>(null)

    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [progress, setProgress] = useState(0)
    const [status, setStatus] =
        useState<"idle" | "uploading" | "success" | "error">("idle")

    const isDisabled =
        !productUploadNameRef?.current?.value || productCatError


    const processFile = (file: File) => {
        setPreviewUrl(URL.createObjectURL(file))
        setStatus("uploading")
        setProgress(0)

        let current = 0
        const interval = setInterval(() => {
            current += 10
            setProgress(current)

            if (current >= 100) {
                clearInterval(interval)

                setStatus("success")

                setImageUploadData({
                    ProductName: productUploadNameRef?.current?.value ?? "",
                    CategoryName: categoryToUpload?.value ?? "",
                    ImageFile: file,
                    OtherNames: productUploadOtherNames,
                })

                setUploadProductName("")

            }
        }, 120)
    }

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0]
        if (!file) return
        if (file.size > 1098683) {
            setStatus("error")
            alert("File size exceeds 1MB limit.")
            e.target.value = "" 
            return
        }
        processFile(file)
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        if (isDisabled) return

        const file = e.dataTransfer.files?.[0]
        if (file) processFile(file)
    }


    return (
        <div
            className={`upload_img_wrap ${isDisabled ? "disabled_wrap" : ""}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => !isDisabled && inputRef.current?.click()}
        >
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
            />

            {status === "idle" && (
                <div className="upload_msg">
                    <img src={UploadIcon} alt="upload" />
                    <div className="upload-txt">Upload photo here</div>
                    <div className="or-txt">or</div>
                    <div className="upload-cta">Browse files</div>
                </div>
            )}

            {previewUrl && (
                <div className="upload-preview">
                    <img src={previewUrl} alt="preview" />

                    {status === "uploading" && (
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    )}

                    {status === "success" && (
                        <div className="upload-success">
                            <FaCheckCircle />
                            <span>Uploading to server...</span>
                        </div>
                    )}

                    {status === "error" && (
                        <div className="upload-error">
                            <FaTimesCircle />
                            <span>Upload failed</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default ImageUploadBox
